import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import useActionDuration from '../../../../../common/hooks/useActionDuration';
import { getAnnotationStyle } from '../../../../../common/utils/utils';
import { isVideoLabelingM4EnabledAtom } from '../../../../labelling-v2/atoms/m4-atoms';
import { useStageAtoms } from '../../../../labelling-v2/components/stage-scope/hooks';
import { useDynamicAttributesText } from '../../../../labelling-v2/hooks/use-dynamic-attributes-text';
import type { ANNOTATION_SHAPE_STYLING_TYPE } from '../../../constants';
import { ANNOTATION_TYPE, STAGE_EVENTS } from '../../../constants';
import useStage from '../../../providers/hooks/use-stage';
import useTutorial from '../../../providers/hooks/use-tutorials';
import { type StagePointerEventT } from '../../../providers/stage-provider';
import { POLYGON_TUTORIAL_CONFIG } from '../../../tutorials/polygon-tutorial';
import { TOOLBAR_TUTORIAL } from '../../../tutorials/toolbar-tutorial';
import { type PolygonAnnotationT } from '../../../types';
import { isLabelingMode } from '../../toolbar/utils';
import { GhostPointRenderer } from '../ghost-point-renderer/ghost-point-renderer';
import Shape from '../shape/shape';
import type { VertexT } from '../shape/vertex';
import { VERTEX_TYPE } from '../shape/vertex';
import { checkIfCommonPoint, clampPointToStageBounds, constrainPointsToStageBounds, getPointDistance, getStylesForStylingType, getTransformedPolyPointsOnVertexMove, getTranslatedPolyPoints } from '../utils';
const POLYGON_TOOLBAR_TUTORIAL_ID = 'select-tool-POLYGON';
type PropsT = {
  isDrawing?: boolean;
  annotation?: PolygonAnnotationT;
  annotationIdx?: string;
  disableChanges?: boolean;
  stylingType?: (typeof ANNOTATION_SHAPE_STYLING_TYPE)[keyof typeof ANNOTATION_SHAPE_STYLING_TYPE];
};
const REPEAT_POINT_DELTA = 10;
const Polygon = ({
  isDrawing,
  annotation,
  annotationIdx,
  disableChanges,
  stylingType
}: PropsT) => {
  const {
    stage,
    addAnnotation,
    updateAnnotation,
    deleteAnnotation,
    getStageCenter,
    getStageDimension
  } = useStage();
  const {
    editorModeAtom,
    editableAnnotationAtom,
    focusedAnnotationIdAtom,
    selectedAnnotationLabelAtom
  } = useStageAtoms();
  const editorMode = useAtomValue(editorModeAtom);
  const editableAnnotation = useAtomValue(editableAnnotationAtom);
  const focusedAnnotationId = useAtomValue(focusedAnnotationIdAtom);
  const annotationLabel = useAtomValue(selectedAnnotationLabelAtom);
  const isVideoLabelingM4Enabled = useAtomValue(isVideoLabelingM4EnabledAtom);
  const polygonRef = useRef<SVGPolygonElement | null>(null);
  const [labelCoords, setLabelCoords] = useState(null);
  const [drawingPolygon, setDrawingPolygon] = useState<PolygonAnnotationT | undefined | null>(annotation);
  const [selectedVertexIdx, setSelectedVertexIdx] = useState<number | null>(null);
  const [tempMovePoint, setTempMovePoint] = useState<[number, number] | null>(null);
  const isEditable = editableAnnotation === annotationIdx;
  const isFocused = focusedAnnotationId === annotationIdx;
  const [ghostVertex, setGhostVertex] = useState<VertexT | null>(null);
  const {
    start,
    end
  } = useActionDuration();
  const isLocked = Boolean(annotation?.locked);
  const {
    addStep,
    next,
    currentActiveTutorial
  } = useTutorial();
  useEffect(() => {
    const rootTutorialId = TOOLBAR_TUTORIAL[POLYGON_TOOLBAR_TUTORIAL_ID]?.id;
    if (isDrawing && currentActiveTutorial === rootTutorialId && isLabelingMode(editorMode)) {
      rootTutorialId && addStep({
        ...POLYGON_TUTORIAL_CONFIG.drawPolygon,
        getTarget: () => polygonRef.current
      }, rootTutorialId);
    }
  }, [isDrawing, addStep, currentActiveTutorial, editorMode]);
  useEffect(() => {
    setDrawingPolygon(annotation);
  }, [annotation]);
  useEffect(() => {
    if (polygonRef.current && !isDrawing) {
      const box = polygonRef.current.getBBox();

      // @ts-expect-error missing null check
      setLabelCoords([box.x + box.width / 2, box.y + box.height / 2]);
    }
  }, [isDrawing, drawingPolygon]);
  const onPointerDown = useCallback((stageEvent: StagePointerEventT) => {
    const {
      mousePos,
      scale
    } = stageEvent.detail;
    // TODO: handle case where annotationLabel is not selected
    if (!annotationLabel) return;
    const points = drawingPolygon?.data.points ? [...drawingPolygon.data.points] : [];
    if (points.length === 0) {
      start();
    }
    const initialPoint = drawingPolygon?.data.points && drawingPolygon?.data.points?.length > 0 ? drawingPolygon?.data.points[0] : null;
    const polygon = {
      annotationType: ANNOTATION_TYPE.POLYGON,
      annotationLabel,
      data: {
        points
      }
    };
    if (initialPoint && points.length === 2) {
      next(POLYGON_TUTORIAL_CONFIG.drawPolygon.id);
      const rootTutorialId = TOOLBAR_TUTORIAL['select-tool-POLYGON']?.id;
      rootTutorialId && addStep({
        ...POLYGON_TUTORIAL_CONFIG.endPolygon,
        getTarget: () => polygonRef.current
      }, rootTutorialId);
    }
    if (initialPoint && checkIfCommonPoint(mousePos, initialPoint, scale)) {
      // If the initial point is clicked again and number of points are at least 3, close the polygon
      if (points.length > 2) {
        setDrawingPolygon(null);
        setTempMovePoint(null);
        addAnnotation(polygon, {
          actionDuration: end(),
          vertex: points.length
        });
        next(POLYGON_TUTORIAL_CONFIG.endPolygon.id);
      }
    } else {
      // Clamp the point to stage bounds to prevent negative or out-of-bounds coordinates
      const [stageWidth, stageHeight] = getStageDimension();
      const clampedPoint = clampPointToStageBounds(mousePos, stageWidth, stageHeight);
      // set the drawing polygon
      points.push(clampedPoint);
      setDrawingPolygon(polygon);
    }
  }, [annotationLabel, drawingPolygon?.data.points, start, next, addStep, addAnnotation, end, getStageDimension]);
  const onPointerMove = useCallback((stageEvent: StagePointerEventT) => {
    if (!drawingPolygon) return;
    const {
      mousePos,
      shiftKey,
      mouseLeftDown,
      scale
    } = stageEvent.detail;
    let points = [...drawingPolygon.data.points];
    const lastCommittedPoint = points[points.length - 1];

    // Clamp mouse position to stage bounds
    const [stageWidth, stageHeight] = getStageDimension();
    const clampedMousePos = clampPointToStageBounds(mousePos, stageWidth, stageHeight);

    // Calculate delta from last committed point
    // @ts-expect-error todo(ts-migration) TS2345 Argument of type '[number, number] | undefined' is not assignable to parameter of type 'MousePosT'.
    const distance = getPointDistance(clampedMousePos, lastCommittedPoint);

    // TODO: remove shiftKey and use only left pointer to add continuous points
    // Add point before temp move point
    const scaledRepeatPointDelta = REPEAT_POINT_DELTA / scale; // calculate delta based on scale
    if ((shiftKey || mouseLeftDown) && distance > scaledRepeatPointDelta) {
      points.push(clampedMousePos);
      setDrawingPolygon({
        ...drawingPolygon,
        data: {
          points
        }
      });
    }
    setTempMovePoint(clampedMousePos);
  }, [drawingPolygon, getStageDimension]);
  const onDrag = useCallback((stageEvent: StagePointerEventT) => {
    if (drawingPolygon && annotation) {
      // For move drag, if the target is not same ignore the event.
      if (selectedVertexIdx === null && stageEvent.detail.dragTarget !== polygonRef.current) {
        return;
      }
      const [stageWidth, stageHeight] = getStageDimension();
      const newPoints = selectedVertexIdx !== null ? getTransformedPolyPointsOnVertexMove(stageEvent.detail.mousePos, annotation.data.points, selectedVertexIdx) : getTranslatedPolyPoints(stageEvent.detail.deltaX, stageEvent.detail.deltaY, annotation.data.points);

      // Constrain all points to stay within stage bounds during drag
      const {
        points: constrainedPoints
      } = constrainPointsToStageBounds(newPoints, stageWidth, stageHeight);
      setDrawingPolygon({
        ...drawingPolygon,
        data: {
          points: constrainedPoints
        }
      });
      start();
    }
  }, [selectedVertexIdx, annotation, drawingPolygon, start, getStageDimension]);
  const onDragEnd = useCallback((stageEvent: StagePointerEventT) => {
    if (annotation && annotationIdx !== undefined) {
      // For move drag, if the target is not same ignore the event.
      if (selectedVertexIdx === null && stageEvent.detail.dragTarget !== polygonRef.current) {
        return;
      }
      const [stageWidth, stageHeight] = getStageDimension();
      const newPoints = selectedVertexIdx !== null ? getTransformedPolyPointsOnVertexMove(stageEvent.detail.mousePos, annotation.data.points, selectedVertexIdx) : getTranslatedPolyPoints(stageEvent.detail.deltaX, stageEvent.detail.deltaY, annotation.data.points);

      // Constrain all points to stay within stage bounds during drag
      const {
        points: constrainedPoints
      } = constrainPointsToStageBounds(newPoints, stageWidth, stageHeight);
      updateAnnotation(annotationIdx, {
        ...annotation,
        data: {
          points: constrainedPoints
        }
      }, {
        actionDuration: end(),
        vertex: constrainedPoints.length
      });
      setSelectedVertexIdx(null);
    }
  }, [annotation, annotationIdx, selectedVertexIdx, updateAnnotation, end, getStageDimension]);
  const onVertexRightClick = useCallback(() => {
    if (isDrawing) {
      const points = drawingPolygon?.data.points ? [...drawingPolygon.data.points] : [];
      if (!annotationLabel) {
        console.error('annotationLabel is undefined, cannot create polygon.');
        return;
      }
      points.pop();
      if (points.length === 0) {
        setDrawingPolygon(null);
        return;
      }
      const polygon: PolygonAnnotationT = {
        annotationType: ANNOTATION_TYPE.POLYGON,
        annotationLabel,
        data: {
          points
        }
      };
      setDrawingPolygon(polygon);
    } else if (selectedVertexIdx !== null) {
      if (!annotation || !annotationIdx) return;
      const originalPoints = annotation.data.points;
      const newPoints = originalPoints.slice(0, selectedVertexIdx).concat(originalPoints.slice(selectedVertexIdx + 1, originalPoints.length));
      if (newPoints.length === 2) {
        deleteAnnotation(annotationIdx, {
          actionDuration: end()
        });
        return;
      }
      updateAnnotation(annotationIdx, {
        ...annotation,
        data: {
          points: newPoints
        }
      }, {
        actionDuration: end(),
        vertex: newPoints.length
      });
      setSelectedVertexIdx(null);
    }
  }, [isDrawing, drawingPolygon, annotationLabel, annotation, selectedVertexIdx, updateAnnotation, annotationIdx, end, deleteAnnotation]);
  const onAddNewPoint = useCallback((newPoints: [number, number][]) => {
    if (!annotationIdx || !annotation) {
      return;
    }
    updateAnnotation(annotationIdx, {
      ...annotation,
      data: {
        points: newPoints
      }
    }, {
      actionDuration: end(),
      vertex: newPoints.length
    });
  }, [annotation, annotationIdx, end, updateAnnotation]);
  useEffect(() => {
    if (!stage || !isLabelingMode(editorMode)) return;
    if (isDrawing) {
      stage.addEventListener('pointerdown', onPointerDown);
      stage.addEventListener('pointermove', onPointerMove);
      return () => {
        stage.removeEventListener('pointerdown', onPointerDown);
        stage.removeEventListener('pointermove', onPointerMove);
      };
    }
  }, [isDrawing, stage, onPointerDown, onPointerMove, editorMode]);

  // Added to ensure that event listener is registered every time isEditable & selectedVertexIdx
  // change so that we can access the right annotation in onVertexRightClick callback
  useEffect(() => {
    if (!stage || !isLabelingMode(editorMode)) return;
    stage.addEventListener(STAGE_EVENTS.RIGHT_CLICK, onVertexRightClick);
    return () => {
      stage.removeEventListener(STAGE_EVENTS.RIGHT_CLICK, onVertexRightClick);
    };
  }, [stage, onVertexRightClick, editorMode]);
  useEffect(() => {
    if (!stage || disableChanges || isLocked || !isLabelingMode(editorMode)) return;
    if (isEditable) {
      stage.addEventListener('drag', onDrag);
      stage.addEventListener('dragend', onDragEnd);
      return () => {
        stage.removeEventListener('drag', onDrag);
        stage.removeEventListener('dragend', onDragEnd);
      };
    }
  }, [isEditable, stage, onDrag, onDragEnd, disableChanges, editorMode, isLocked]);
  const polygon = drawingPolygon;
  const dynamicAttributesText = useDynamicAttributesText(annotationIdx);
  const polygonStyle = useMemo(() => getAnnotationStyle({
    fill: polygon?.annotationLabel.style.fillColor,
    fillOpacity: polygon?.annotationLabel.style.fillOpacity,
    stroke: polygon?.annotationLabel.style.strokeColor,
    ...getStylesForStylingType(stylingType)
  }, {
    isFocused,
    isLocked,
    useStageStrokeWidth: true,
    isM4: isVideoLabelingM4Enabled
  }), [polygon?.annotationLabel.style.fillColor, polygon?.annotationLabel.style.fillOpacity, polygon?.annotationLabel.style.strokeColor, stylingType, isFocused, isLocked, isVideoLabelingM4Enabled]);
  const edgeRenderer = useCallback(({
    points,
    onMouseEnter,
    onMouseLeave,
    onClick
  }: {
    points: string;
    onMouseEnter?: React.MouseEventHandler<SVGPolygonElement>;
    onMouseLeave?: React.MouseEventHandler<SVGPolygonElement>;
    onClick?: React.MouseEventHandler<SVGPolygonElement>;
  }) => {
    return <polygon points={points} style={{
      ...polygonStyle,
      fill: 'none',
      cursor: 'pointer'
    }} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onClick={onClick} data-stage-custom />;
  }, [polygonStyle]);
  if (!polygon) {
    const stageCenter = getStageCenter();
    return <polygon ref={polygonRef} points={`${stageCenter[0]},${stageCenter[1]}`} style={polygonStyle}></polygon>;
  }
  const points = polygon.data.points;
  let polygonPoints = points.map(([x, y]) => `${x},${y}`).join(' ');
  if (isDrawing && tempMovePoint) {
    polygonPoints += ` ${tempMovePoint[0]},${tempMovePoint[1]}`;
  }
  return <Shape vertices={points.map(point => ({
    point,
    type: VERTEX_TYPE.MOVE
  }))} annotationLabel={{
    ...polygon.annotationLabel,
    labelCoords
  }} isLocked={isLocked} isDrawing={isDrawing} annotationIdx={annotationIdx} metadataPillText={dynamicAttributesText} onVertexSelect={(_vertex, idx) => setSelectedVertexIdx(idx)} ghostVertices={ghostVertex ? [ghostVertex] : []} dataAttributes={{
    'data-e2e-id': `annotation-${polygon.annotationLabel.title}-${polygon.annotationType}`,
    'data-annotation-id': annotationIdx?.toString() || ''
  }}>
      <polygon ref={polygonRef} points={polygonPoints} style={polygonStyle}></polygon>
      {isLabelingMode(editorMode) && <GhostPointRenderer<SVGPolygonElement> isSelected={isEditable} isDrawing={isDrawing} points={points} ghostVertex={ghostVertex} edgeRenderer={edgeRenderer} onAddNewPoints={onAddNewPoint} onGhostVertexChange={setGhostVertex} />}
    </Shape>;
};
export default Polygon;